import * as elementUtils from "@/common/utils/elementUtils";
import { experienceStore } from "@/stores/experience/store";
import config from "@/modules/config";
import { isUmamiEnabled, umamiTrack } from "@/modules/umami";
import { isAdminPreviewSurface } from "@/modules/previewSurface";
import { readMcpAppProps } from "@/modules/mcpScope";
import type { RequestOptions } from "mixpanel-browser";

export type TrackProps = Record<string, unknown>;

interface QueuedEvent {
  title: string;
  extra: TrackProps | undefined;
  options: RequestOptions | undefined;
}

let queue: QueuedEvent[] = [];
let untilMixpanel: {
  title: string;
  payload: TrackProps;
  options: RequestOptions | undefined;
}[] = [];

export function track(
  title: string,
  extra?: TrackProps,
  options?: RequestOptions,
) {
  if (isAdminPreviewSurface()) {
    return;
  }
  const experienceData = experienceStore.getState().experienceData;
  const metadata = experienceData.metadata;
  if (metadata == null || metadata.idexperience == null) {
    queue.push({
      title: title,
      extra: extra,
      options: options,
    });
  } else {
    if (queue.length > 0) {
      for (let i = 0; i < queue.length; i++) {
        const queuedData = queue[i];
        send(queuedData.title, queuedData.extra, queuedData.options);
      }
      queue = [];
    }

    send(title, extra, options);
  }
}

function send(title: string, extra?: TrackProps, options?: RequestOptions) {
  extra = typeof extra !== "undefined" ? extra : {};
  const experienceData = experienceStore.getState().experienceData;
  const metadata = experienceData.metadata;
  const size = elementUtils.innerWindowSize();

  const data: TrackProps = {
    pix_pixmotouid: metadata.pixmotoUID,
    pix_idaccount: experienceData.idaccount,
    pix_idexperience: metadata.idexperience,
    pix_ipaddress: metadata.ipAddress,
    pix_device: config.device,
    // Player version on every event (Mixpanel context). Injected at build
    // time (scripts/vite/define.mjs → process.env.PIXMOTO_PLAY_VERSION).
    pix_player_version: process.env.PIXMOTO_PLAY_VERSION,
    pix_width: size.width,
    pix_height: size.height,
  };

  if (experienceData.idexperiencebundle) {
    data.pix_idexperiencebundle = experienceData.idexperiencebundle;
  }

  // copy any extra keys
  for (const key in extra) {
    if (Object.hasOwn(extra, key)) {
      data[key] = extra[key];
    }
  }

  if (isUmamiEnabled()) {
    // Mixpanel carries the MCP-App context as super-properties; Umami has no
    // super-property channel, so merge it into this event's own payload.
    umamiTrack(title, Object.assign({}, data, readMcpAppProps()), {
      beacon: !!(options && options.transport === "sendBeacon"),
    });
  }

  if (!window.mixpanel || typeof window.mixpanel.track !== "function") {
    untilMixpanel.push({
      title: title,
      payload: data,
      options: options,
    });
    return;
  }

  if (options) {
    window.mixpanel.track(title, data, options);
  } else {
    window.mixpanel.track(title, data);
  }
}

export function flushMixpanelDeferred() {
  if (!window.mixpanel || typeof window.mixpanel.track !== "function") {
    return;
  }
  if (!untilMixpanel || !untilMixpanel.length) {
    return;
  }
  const pending = untilMixpanel;
  untilMixpanel = [];
  for (let i = 0; i < pending.length; i++) {
    const item = pending[i];
    if (item.options) {
      window.mixpanel.track(item.title, item.payload, item.options);
    } else {
      window.mixpanel.track(item.title, item.payload);
    }
  }
}
